/**
 * decline-home — does this ribbon field need a home in the drawer? (s178, A3 / F3)
 *
 * `ExpandableSectionLayout` splits a record between a compact RIBBON and an expanded DRAWER.
 * A basic (ribbon) field is also rendered in the drawer when it needs an affordance the ribbon
 * has no room for. The decline control is one such affordance.
 *
 * 🔴 THE RULE: a field needs a drawer home iff it is `declineEligible` and not already grouped
 * into the drawer by config. `isRequired` is NOT part of it — that clause is what created F3.
 * Until s178 the injection read `isRequired && declineEligible`, so a declineEligible
 * NON-required basic had no drawer home; combined with a ribbon that rendered the declined
 * state but no control, the decline affordance existed on NO surface. Such a field could not be
 * declined, and an already-declined one could not be undone or re-declined.
 *
 * 🔴 THIS FUNCTION IS THE PREDICATE, not a description of it. `ExpandableSectionLayout` calls it
 * and `decline-home-coverage.test.ts` calls the SAME function, so the guard asserts identity
 * rather than agreeing with a copy — re-adding a narrowing clause here fails the walk instead of
 * quietly re-opening the trap (the DECLARATIVE-SURFACES-1 pattern applied to a render rule).
 */

export interface DeclineHomeField {
  declineEligible?: boolean | null;
}

/**
 * @param field    the basic (ribbon) field
 * @param isGrouped is it already placed in a drawer group by `EXPANDABLE_LAYOUT_CONFIG`?
 */
export function needsDeclineDrawerHome(field: DeclineHomeField, isGrouped: boolean): boolean {
  if (isGrouped) return false; // the drawer already renders it — injecting would duplicate the cell
  return !!field.declineEligible;
}
